"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { closeJobModal } from "@/redux/features/jobModal/jobModalSlice";
import CustomModal from "@/components/modal/CustomModal";
import { motion, AnimatePresence } from "framer-motion";
import {
  handleField,
  ErrorMap,
  ValidationType,
  validateInputData,
} from "@/utils/allValidations";
import Lottie from "lottie-react";
import successAnimation from "@/../public/next_assets/lottie/success-tick.json";
import {
  defaultRoleDescriptions,
  defaultRoleResponsibilities,
  fallbackResponsibilities,
} from "@/utils/jobsData";
import JobModalInput from "../others/JobModalInput";
import { JobApplicationFormData } from "@/type/commonTypes";
import { createJobApplicationFormData } from "@/utils/commonFunctions";
import { submitJobApplication } from "@/utils/commonServerFunctions";

export default function JobApplicationModal() {
  const dispatch = useDispatch();
  const { isOpen, selectedJob } = useSelector(
    (state: RootState) => state.jobModal,
  );

  const [step, setStep] = useState(0);

  const [formData, setFormData] = useState<JobApplicationFormData>({
    fullName: "",
    email: "",
    phone: "",
    expYears: "",
    expMonths: "",
    linkedIn: "",
    coverLetter: "",
  });

  const [errors, setErrors] = useState<ErrorMap>({});

  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setStep(0);
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          expYears: "",
          expMonths: "",
          linkedIn: "",
          coverLetter: "",
        });
        setErrors({});
        setFile(null);
        setSubmitError(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    dispatch(closeJobModal());
  }, [dispatch]);

  // Automatically close modal 3 seconds after success result (step 4)
  useEffect(() => {
    if (step === 4 && isOpen) {
      const timer = setTimeout(() => {
        handleClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [step, isOpen, handleClose]);

  const nextStep = () => {
    if (step === 1) {
      const newErrors: ErrorMap = {};

      const fullName = formData.fullName.trim();
      if (!fullName) {
        newErrors.fullName = "Full name is required";
      } else if (fullName.length > 100) {
        newErrors.fullName = "Full name must not exceed 100 characters";
      } else if (!validateInputData("name", fullName)) {
        newErrors.fullName = "Please enter a valid full name";
      }

      const email = formData.email.trim();
      if (!email) {
        newErrors.email = "Email address is required";
      } else if (email.length > 150) {
        newErrors.email = "Email must not exceed 150 characters";
      } else if (!validateInputData("email", email)) {
        newErrors.email = "Please enter a valid email address";
      }

      const phone = formData.phone.trim();
      if (!phone) {
        newErrors.phone = "Phone number is required";
      } else if (!/^[6-9]\d{9}$/.test(phone)) {
        newErrors.phone = "Please enter a valid 10-digit mobile number.";
      }

      const expY = formData.expYears.trim();
      if (expY === "") {
        newErrors.expYears = "Years of experience is required";
      } else {
        const yNum = Number(expY);
        if (!/^\d+$/.test(expY) || isNaN(yNum) || yNum < 0 || yNum > 50) {
          newErrors.expYears = "Experience must be between 0 and 50 years";
        }
      }

      const expM = formData.expMonths.trim();
      if (expM === "") {
        newErrors.expMonths = "Months of experience is required";
      } else {
        const mNum = Number(expM);
        if (!/^\d+$/.test(expM) || isNaN(mNum) || mNum < 0 || mNum > 11) {
          newErrors.expMonths = "Months must be between 0 and 11";
        }
      }

      if (formData.linkedIn && formData.linkedIn.trim().length > 255) {
        newErrors.linkedIn =
          "LinkedIn profile URL must not exceed 255 characters";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...newErrors }));
        return;
      }
    }

    if (step === 2) {
      const newErrors: ErrorMap = {};

      if (!file) {
        newErrors.file = "Please upload your resume";
      }

      if (formData.coverLetter && formData.coverLetter.trim().length > 5000) {
        newErrors.coverLetter = "Cover letter must not exceed 5000 characters";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...newErrors }));
        return;
      }
    }

    setStep((s) => s + 1);
  };

  const prevStep = () => {
    setSubmitError(null);
    setStep((s) => Math.max(0, s - 1));
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (f: File) => {
    const validExtensions = [".pdf", ".doc", ".docx"];
    const validMimeTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    const isValidType =
      validMimeTypes.includes(f.type) || validExtensions.includes(ext);

    if (!isValidType) {
      setErrors((prev) => ({
        ...prev,
        file: "Resume must be a PDF, DOC or DOCX file.",
      }));
      return;
    }

    // 5 MB = 5120 KB = 5 * 1024 * 1024 bytes
    if (f.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        file: "Resume size must not exceed 5 MB.",
      }));
      return;
    }

    setFile(f);
    setErrors((prev) => {
      const n = { ...prev };
      delete n.file;
      return n;
    });
  };

  const submitApplication = async () => {
    if (!file) {
      setErrors((prev) => ({ ...prev, file: "Please upload your resume" }));
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      const payload = createJobApplicationFormData(formData, file, selectedJob);
      const res = await submitJobApplication(payload);

      if (res?.status == 1) {
        setStep(4);
      } else {
        setSubmitError(
          res?.message || "Application submission failed. Please try again.",
        );
      }
    } catch (error: unknown) {
      console.error("Application submission failed:", error);
      setSubmitError("Application submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    field: keyof typeof formData,
    type: ValidationType,
    val: string,
    allowSpace = false,
    min?: number,
    max?: number,
  ) => {
    handleField({
      type,
      value: val,
      field,
      mode: "change",
      setValue: (v) => setFormData((prev) => ({ ...prev, [field]: v })),
      setErrors,
      prevValue: formData[field],
      allowSpace,
      min,
      max,
    });
  };

  const handleBlur = (
    field: keyof typeof formData,
    type: ValidationType,
    val: string,
    allowSpace = false,
    min?: number,
    max?: number,
  ) => {
    handleField({
      type,
      value: val,
      field,
      mode: "blur",
      setErrors,
      prevValue: formData[field],
      allowSpace,
      min,
      max,
    });
  };

  const job = selectedJob || { title: "", department: "", location: "" };
  const jobDescription =
    job.description ||
    defaultRoleDescriptions[job.title] ||
    `Join a super talented team that is redefining how millions of everyday families save, invest, and compound their wealth through smart technology. We are looking for an experienced ${job.title || "professional"} to drive the experience of our trusted investment platform.`;
  const jobResponsibilities =
    job.responsibilities && job.responsibilities.length > 0
      ? job.responsibilities
      : defaultRoleResponsibilities[job.title] || fallbackResponsibilities;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      maxSize="max-w-lg"
      className="w-full h-600 sm:h-155 flex flex-col overflow-hidden border border-[#EAECF0]"
      isBlurOverlay
      closeOnOverlayClick={false}
    >
      {step !== 4 && (
        <div className="border-b border-[#EAECF0] px-4 py-3 sm:px-6 sm:py-4 shrink-0 bg-white">
          <div className="text-lg sm:text-xl font-bold text-primaryBlack truncate">
            Apply for {job.title || "Job"}
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-hidden relative flex flex-col">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex-1 min-h-0 overflow-y-auto scrollbar-xs p-4 sm:p-6 pr-2 sm:pr-4"
            >
              <div className="text-1.5xl sm:text-2.5xl font-semibold text-primaryBlack mb-3">
                {job.title}
              </div>

              <div className="flex gap-3 mb-4 sm:mb-6">
                {job.department && (
                  <span className="px-3 py-1 bg-[#EBF3FF] text-primaryBlue text-xs font-semibold rounded-xs">
                    {job.department}
                  </span>
                )}
                {job.location && (
                  <span className="px-3 py-1 bg-[#F4F3FF] text-[#51526C] text-xs font-semibold rounded-xs">
                    {job.location}
                  </span>
                )}
              </div>

              <p className="text-[#475467] text-2xs sm:text-2sm sm:leading-5.5 leading-1.5 mb-6">
                {jobDescription}
              </p>

              <div className="font-bold text-primaryBlack mb-2.5">
                Key Responsibilities
              </div>

              <ul className="list-disc pl-5 text-[#475467] text-sm space-y-2">
                {jobResponsibilities.map((resp, idx) => (
                  <li key={idx}>{resp}</li>
                ))}
              </ul>
            </motion.div>
          )}

          {(step === 1 || step === 2 || step === 3) && (
            <motion.div
              key={`formSteps-${step}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex-1 min-h-0 overflow-y-auto scrollbar-xs p-4 sm:p-6 pr-2 sm:pr-4"
            >
              {/* Stepper */}
              <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 shrink-0">
                {["Personal", "Documents", "Review"].map((label, i) => {
                  const stepNum = i + 1;
                  const isActive = step === stepNum;
                  const isPassed = step > stepNum;
                  return (
                    <div key={label} className="flex-1">
                      <div
                        className={`h-1 rounded-full w-full mb-2 transition ${
                          isPassed
                            ? "bg-[#90C63A]"
                            : isActive
                              ? "bg-primaryBlue"
                              : "bg-[#EAECF0]"
                        }`}
                      />
                      <p
                        className={`text-xs transition ${
                          i === 0
                            ? "text-left"
                            : i === 1
                              ? "text-center"
                              : "text-right"
                        } ${isActive ? "text-primaryBlue font-semibold" : isPassed ? "text-[#6E6E6E]" : "text-[#98A2B3]"}`}
                      >
                        {stepNum}. {label}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div>
                {step === 1 && (
                  <div className="space-y-5">
                    <JobModalInput
                      label="Full Name"
                      required
                      placeholder="Write your full name"
                      value={formData.fullName}
                      maxLength={100}
                      onChange={(e) =>
                        handleChange("fullName", "name", e.target.value, true)
                      }
                      onBlur={(e) =>
                        handleBlur("fullName", "name", e.target.value, true)
                      }
                      error={errors.fullName}
                    />

                    <JobModalInput
                      label="Email Address"
                      required
                      type="email"
                      placeholder="Write your email address"
                      value={formData.email}
                      maxLength={150}
                      onChange={(e) =>
                        handleChange("email", "email", e.target.value)
                      }
                      onBlur={(e) =>
                        handleBlur("email", "email", e.target.value)
                      }
                      error={errors.email}
                    />

                    <JobModalInput
                      label="Phone Number"
                      required
                      type="text"
                      placeholder="Write your phone number"
                      value={formData.phone}
                      maxLength={10}
                      onChange={(e) =>
                        handleChange("phone", "phone", e.target.value)
                      }
                      onBlur={(e) =>
                        handleBlur("phone", "phone", e.target.value)
                      }
                      error={errors.phone}
                    />

                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-primaryBlack mb-1.5">
                        Years of Experience{" "}
                        <span className="text-[#EC4D4D]">*</span>
                      </label>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <JobModalInput
                            placeholder="Years"
                            value={formData.expYears}
                            maxLength={2}
                            onChange={(e) =>
                              handleChange(
                                "expYears",
                                "number",
                                e.target.value,
                                false,
                                0,
                                50,
                              )
                            }
                            onBlur={(e) =>
                              handleBlur(
                                "expYears",
                                "number",
                                e.target.value,
                                false,
                                0,
                                50,
                              )
                            }
                            error={errors.expYears}
                            hideErrorMessage
                          />
                        </div>
                        <div className="flex-1">
                          <JobModalInput
                            placeholder="Months"
                            value={formData.expMonths}
                            maxLength={2}
                            onChange={(e) =>
                              handleChange(
                                "expMonths",
                                "number",
                                e.target.value,
                                false,
                                0,
                                11,
                              )
                            }
                            onBlur={(e) =>
                              handleBlur(
                                "expMonths",
                                "number",
                                e.target.value,
                                false,
                                0,
                                11,
                              )
                            }
                            error={errors.expMonths}
                            hideErrorMessage
                          />
                        </div>
                      </div>
                      {(errors.expYears || errors.expMonths) && (
                        <p className="text-[#EC4D4D] text-xs mt-1">
                          {errors.expYears ||
                            errors.expMonths ||
                            "Please enter valid experience"}
                        </p>
                      )}
                    </div>

                    <JobModalInput
                      label="LinkedIn URL"
                      placeholder="Write your LinkedIn profile URL"
                      value={formData.linkedIn}
                      maxLength={255}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, linkedIn: e.target.value }))
                      }
                      error={errors.linkedIn}
                    />
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-primaryBlack mb-1.5">
                        Resume / CV <span className="text-[#EC4D4D]">*</span>
                      </label>
                      <label
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleFileDrop}
                        className={[
                          "flex flex-col items-center justify-center p-7 border-2 border-dashed rounded-xl cursor-pointer transition",
                          isDragging
                            ? "border-primaryBlue bg-blue-50"
                            : errors.file
                              ? "border-[#EC4D4D]"
                              : "border-blue-300 sm:hover:bg-gray-50",
                        ].join(" ")}
                      >
                        <span className="text-primaryBlue font-semibold text-2sm mb-1">
                          {file ? file.name : "Drag & drop or browse"}
                        </span>
                        <span className="text-[#6E6E6E] text-xs">
                          {file
                            ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                            : "PDF, DOC or DOCX (Max 5MB)"}
                        </span>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={(e) =>
                            e.target.files &&
                            handleFileChange(e.target.files[0])
                          }
                        />
                      </label>
                      {errors.file && (
                        <p className="text-[#EC4D4D] text-xs mt-1">
                          {errors.file}
                        </p>
                      )}
                    </div>

                    <JobModalInput
                      label="Cover Letter (Optional)"
                      multiline
                      rows={4}
                      maxLength={5000}
                      placeholder="Introduce yourself to the team..."
                      value={formData.coverLetter}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          coverLetter: e.target.value,
                        }))
                      }
                      error={errors.coverLetter}
                    />
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-4">
                    <div className="font-bold text-primaryBlack">
                      Review your information
                    </div>
                    <div className="bg-[#FAF8F5] p-4 rounded-xl border border-[#EAECF0] space-y-3.5">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primaryBlue text-white rounded-full flex shrink-0 items-center justify-center text-xl font-bold uppercase">
                          {formData.fullName.charAt(0)}
                        </div>
                        <div className="overflow-hidden">
                          <p className="font-medium text-primaryBlack truncate">
                            {formData.fullName}
                          </p>
                          <p
                            title={`${formData.email} - ${formData.phone}`}
                            className="text-[#475467] text-2xs mt-0.5 truncate"
                          >
                            {formData.email} &bull; {formData.phone}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-1 font-medium text-sm">
                        <p className="text-primaryBlack">
                          {formData.expYears}Y - {formData.expMonths}M{" "}
                          <i className="text-primaryBlack/70">of Experience</i>
                        </p>
                        {formData.linkedIn && (
                          <p className="text-primaryBlue break-all">
                            {formData.linkedIn}
                          </p>
                        )}
                      </div>

                      {file && (
                        <div className="flex justify-between items-center border border-[#D0D5DD] rounded-md py-2.5 px-3 bg-white">
                          <p className="text-sm leading-1.2 font-semibold text-primaryBlack truncate mr-3">
                            {file.name}
                          </p>
                          <p className="text-xs text-[#6E6E6E] shrink-0">
                            {(file.size / 1024 / 1024).toFixed(1)} MB
                          </p>
                        </div>
                      )}

                      {formData.coverLetter && (
                        <div>
                          <p className="text-xs font-bold text-[#6E6E6E] uppercase tracking-wider mb-1">
                            COVER LETTER
                          </p>
                          <p className="text-2xs text-[#475467] italic line-clamp-2">
                            &quot;{formData.coverLetter}&quot;
                          </p>
                        </div>
                      )}
                    </div>

                    {submitError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-[#EC4D4D] text-xs font-medium">
                        {submitError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex-1 min-h-0 flex flex-col items-center justify-center p-6 sm:p-8 text-center"
            >
              <Lottie
                className="w-32 h-32 mx-auto"
                animationData={successAnimation}
                loop={true}
                autoplay
              />
              <div className="text-2xl font-bold text-primaryBlack mb-4">
                Application Submitted!
              </div>
              <p className="text-[#555860] text-sm leading-relaxed mb-8 max-w-sm mx-auto">
                Thank you for applying! We&apos;ve sent a confirmation email to{" "}
                <span className="font-bold text-primaryBlack">
                  {formData.email}
                </span>
                . Our team will review your application and get back to you
                shortly.
              </p>
              <button
                onClick={handleClose}
                className="w-full max-w-xs px-6 py-3 bg-primaryBlue text-white rounded-lg font-medium sm:hover:bg-blue-700 transition"
              >
                Done
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {step !== 4 && (
        <div className="flex justify-end gap-3 p-4 sm:p-5 border-t border-[#EAECF0] bg-[#FAF8F5] shrink-0">
          {step === 0 ? (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2.5 sm:px-6 sm:py-3 border border-[#D0D5DD] rounded-lg text-[#344054] text-2sm leading-1.3 font-semibold transition sm:hover:bg-primaryBlack/15"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-4 py-2.5 sm:px-6 sm:py-3 border border-primaryBlue bg-primaryBlue rounded-lg text-white text-2sm leading-1.3 font-semibold transition sm:hover:bg-transparent sm:hover:text-primaryBlue sm:hover:border-primaryBlue"
              >
                Apply Now
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={prevStep}
                disabled={isSubmitting}
                className="px-4 py-2.5 sm:px-6 sm:py-3 border border-[#D0D5DD] rounded-lg text-[#344054] text-2sm leading-1.3 font-semibold transition sm:hover:bg-primaryBlack/15 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={step === 3 ? submitApplication : nextStep}
                disabled={isSubmitting}
                className="px-4 py-2.5 sm:px-6 sm:py-3 border border-primaryBlue bg-primaryBlue rounded-lg text-white text-2sm leading-1.3 font-semibold transition sm:hover:bg-transparent sm:hover:text-primaryBlue sm:hover:border-primaryBlue disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : step === 3 ? (
                  "Submit Application"
                ) : (
                  "Continue"
                )}
              </button>
            </>
          )}
        </div>
      )}
    </CustomModal>
  );
}
